import { computed, nextTick } from 'vue';
import {
  getAssetContainerStatusIcon,
  getAssetContainerStatusColor,
  getAssetContainerStateIcon
} from '@satellite/helpers/yardTaskUtils';
import { useYardAssetStatus } from '@satellite/composables/useYardAssetStatus';
import { useRoute, useRouter } from 'vue-router';
import { isUUID } from 'class-validator';
import { useStore } from 'vuex';
import { camelCase, get, isString } from 'lodash';
import { DateTime } from 'luxon';
import { useAuth, useEventHub, useCustomForms, useSelectedWarehouse } from '@/composables';
import { useYardAnalytics } from '@/composables/useYardAnalytics';
import { useYardSpotGridLookup } from '@satellite/composables/yard/useYardSpotGridLookup';
import {
  AssetVisitType,
  SpotType,
  SPOT_STATUSES_RESERVED,
  SpotStatus,
  getSpotStatus,
  LuxonDateTimeFormats,
  isSpotAssignedToAssetVisit,
  isSpotAssignedToAssetContainer,
  isSpotAssignedOnlyToAssetContainer,
  isSpotAssignedOnlyToAssetVisit,
  canUserCreateSpotAssignment,
  formatDateTimeWithMilitarySupport,
  YARD_SPOT_CODE_MAX_LENGTH,
  AppointmentStatus,
  sortSpots,
  LoadTypeDirection,
  AssetContainerEventType,
  isFeatureEnabledForWarehouse,
  WarehouseFeatureEnum,
  YardViewModeEnum,
  canUserCreateSpot,
  isUserRoleGreaterOrEqual,
  UserRole,
  isUserRoleLessOrEqual,
  getDirectionForVisitType,
  AssetVisitDirection
} from '@nova/core';
import { useI18n } from 'vue-i18n';

/** Key of the column after which custom (asset detail) columns are rendered in the list view. */
export const CUSTOM_COLUMNS_AFTER_KEY = 'spotCode';

/** Budget for the yard board to come up and hold the target spot, in ms. */
const GO_TO_SPOT_TIMEOUT_MS = 6000;
const GO_TO_SPOT_POLL_INTERVAL_MS = 400;

/**
 * Returns standard columns with custom columns inserted immediately after the anchor column.
 * Used by list view (header order) and column select (dropdown order).
 */
export function placeCustomColumnsAfterAnchor(
  standardColumns,
  customColumns,
  anchorKey = CUSTOM_COLUMNS_AFTER_KEY
) {
  const anchorIndex = standardColumns.findIndex(c => c.key === anchorKey);
  if (anchorIndex === -1 || !customColumns?.length) {
    return [...standardColumns, ...(customColumns || [])];
  }
  return [
    ...standardColumns.slice(0, anchorIndex + 1),
    ...customColumns,
    ...standardColumns.slice(anchorIndex + 1)
  ];
}

export default function useYard() {
  const { t, locale } = useI18n();
  const assetStatus = useYardAssetStatus();
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const yardAnalytics = useYardAnalytics();
  /*
   * Both must be created here, in the setup context: `useYardSpotGridLookup` injects the
   * TanStack query client, and after the `await router.push` in `goToSpotFromOtherModules`
   * there is no injection context left for it.
   */
  const spotGridLookup = useYardSpotGridLookup();
  const { $emit } = useEventHub();
  const { selectedWarehouseId } = useSelectedWarehouse();
  const { getFormattedValue } = useCustomForms();

  // Event payload: { assetVisit: Object, newSpotCode: string, newSpotAreaName: string }
  const SPOT_ASSIGNMENT_CHANGED = 'spot-assignment-changed';

  // Event payload: { newSpotCode: string }
  const GO_TO_SPOT = 'go-to-spot';

  // Event payload: void (signals a new check-in arrived via websocket)

  const storePath = 'state.Yard';
  const spotCardWidth = 60;
  const spotCardHeight = 170;
  const getSpotIcon = type => {
    return type === SpotType.DOCKING ? 'mdi-truck-outline' : 'mdi-parking';
  };

  const getTruckIcon = type => {
    if (type === AssetVisitType.PickUp) {
      return 'mdi-truck-flatbed';
    }

    if (type === AssetVisitType.Drop || type === AssetVisitType.DropHook) {
      return 'mdi-truck-cargo-container';
    }

    return 'mdi-truck-outline';
  };

  const isYMSEnabledForSelectedWarehouse = computed(
    () =>
      Boolean(selectedWarehouse.value?.id) &&
      isFeatureEnabledForWarehouse(selectedWarehouse.value, WarehouseFeatureEnum.YMS)
  );

  const isGateManagementEnabledForSelectedWarehouse = computed(
    () =>
      Boolean(selectedWarehouse.value?.id) &&
      isFeatureEnabledForWarehouse(selectedWarehouse.value, WarehouseFeatureEnum.GateManagement)
  );

  const getArrivedAssetContainerEvent = assetContainer => {
    if (!assetContainer?.assetContainerEvents?.length) {
      return ' - ';
    }

    return assetContainer.assetContainerEvents.find(
      event =>
        event?.eventType === AssetContainerEventType.Arrived &&
        Boolean(event.assetVisitId || event.assetVisit?.id)
    );
  };

  const formatHour = value =>
    formatDateTime(
      value,
      LuxonDateTimeFormats.Extended12HrTimeAMPM,
      LuxonDateTimeFormats.Extended24HrTime
    );

  const getAssignedAssetContainerStatus = assetContainerEvents =>
    assetStatus.getDisplayStatus(assetContainerEvents);

  const isAssetContainer = asset =>
    asset && isString(asset.code) && Boolean(asset.type) && !Object.hasOwn(asset, 'visitType');

  const getAssignedSpotIcon = loadtypeDirection => {
    if (loadtypeDirection) {
      if (loadtypeDirection === LoadTypeDirection.Inbound) {
        return 'mdi-menu-up';
      }

      if (loadtypeDirection === LoadTypeDirection.Outbound) {
        return 'mdi-menu-down';
      }

      if (loadtypeDirection === LoadTypeDirection.InOut) {
        return 'mdi-menu-swap';
      }
    }

    return null;
  };

  const getLoadTypeDirectionColor = loadTypeDirection => {
    if (loadTypeDirection === LoadTypeDirection.Inbound) {
      return 'info';
    }
    if (loadTypeDirection === LoadTypeDirection.Outbound) {
      return 'warning';
    }

    return 'text--color-black';
  };

  const relativeTimeFromWarehouse = date => {
    const zone = selectedWarehouse.value.timezone;
    return DateTime.fromISO(date, { zone })
      .setLocale(locale.value)
      .toRelative({ base: DateTime.now().setZone(zone) })
      .replace('ago', '');
  };

  const goToSpotFromOtherModules = async ({ spotId, spotCode, warehouseId }) => {
    if (!spotId || !spotCode || !warehouseId) {
      throw new Error('Invalid parameters for goToSpotFromOtherModules');
    }

    await router.push({
      name: 'yard-warehouse',
      params: {
        warehouseId
      }
    });

    /*
     * The route param is an output of the warehouse selection, not an input to it: the board's
     * presenters key on the globally selected warehouse (`GlobalWarehouseSelect` -> the Pinia
     * store), which never reads `route.params.warehouseId`. So pushing `/yard/<id>` does not make
     * the board load that warehouse, and waiting for a spot that its cache will never hold just
     * burns the deadline. Bail out with something the console can act on instead.
     */
    if (selectedWarehouseId.value && selectedWarehouseId.value !== warehouseId) {
      throw new Error(
        `Cannot go to spot ${spotCode}: the board is on warehouse ${selectedWarehouseId.value}, ` +
          `the spot belongs to ${warehouseId}. The warehouse selector has to switch first.`
      );
    }

    /*
     * The board owns the spot-grid query from here on, so the target spot is resolved from that
     * shared cache rather than from a list this composable fetches. The wait is still needed:
     * `YardViewGridView` only subscribes to `GO_TO_SPOT` on mount and expands the panel by
     * looking the code up in the areas it already holds, so emitting before the board has
     * rendered its first grid is a no-op.
     *
     * A deadline rather than a fixed number of attempts: a cold click waits on the warehouse
     * fetch, an async component chunk and the spot-grid request, sequentially.
     */
    const deadline = performance.now() + GO_TO_SPOT_TIMEOUT_MS;

    await new Promise((resolve, reject) => {
      const checkLoading = async () => {
        const spotItem = spotGridLookup.getSpotItemById(warehouseId, spotId);

        /*
         * Asset visits are deliberately not part of this: the emit only tells the grid which area
         * panel to expand, and that is resolved by spot code alone.
         */
        if (!spotItem || selectedAreas.value.length === 0) {
          if (performance.now() >= deadline) {
            reject(new Error(`Timed out waiting for the yard board to load spot ${spotCode}`));
            return;
          }

          setTimeout(pollOnce, GO_TO_SPOT_POLL_INTERVAL_MS);
          return;
        }

        selectedSpot.value = spotItem;

        viewMode.value = YardViewModeEnum.GRID;

        yardAnalytics.trackYardGoToSpotClicked({
          spot: { id: spotId, code: spotCode },
          entryPoint: 'Appointment Details'
        });

        /*
         * The view-mode flip is a Vuex commit, so the grid only mounts on the next render tick,
         * and `$emit` is synchronous. Emitting in the same tick would miss the listener the grid
         * registers `onBeforeMount` — which is exactly what happens to a user whose persisted view
         * mode is the list.
         */
        await nextTick();

        $emit(GO_TO_SPOT, {
          newSpotCode: spotCode
        });

        resolve();
      };

      /*
       * `checkLoading` is `async` and runs both here and from a timer, so nothing on the stack
       * would observe a rejection: the outer promise would neither resolve nor reject and the
       * caller's `await` would hang with no toast. Reachable in practice — mitt's `safeEmit` calls
       * `GO_TO_SPOT` listeners without a guard of its own, and `expandPanelForSpotCode` persists
       * the open panels to localStorage. Any failed iteration fails the navigation instead.
       */
      const pollOnce = () => {
        checkLoading().catch(reject);
      };

      pollOnce();
    });
  };

  const truckThemeColors = {
    blue: {
      backgroundColor: '#E4F1F7',
      borderColor: '#005AD8'
    },
    red: {
      backgroundColor: '#FAEBEE',
      borderColor: '#D40047'
    },
    grey: {
      backgroundColor: '#F2F2F2',
      borderColor: '#656565'
    }
  };

  const getTruckThemeColor = assetVisit => {
    const appointment = assetVisit?.appointment;
    const warehouseNow = DateTime.now().setZone(selectedWarehouse.value.timezone);

    if (appointment?.id) {
      if (
        appointment.status == AppointmentStatus.Cancelled ||
        appointment.status == AppointmentStatus.NoShow ||
        // If appointment end time is in the past, we should consider it as cancelled
        warehouseNow > DateTime.fromISO(appointment.end)
      ) {
        return { ...truckThemeColors.red, theme: 'red' };
      }

      // If it is an active appointment and not ended yet, show as blue
      return { ...truckThemeColors.blue, theme: 'blue' };
    }

    // When no appointment, show as grey
    return { ...truckThemeColors.grey, theme: 'grey' };
  };

  const isMilitaryTimeEnabled = computed(() =>
    store.getters['Settings/isMilitaryTimeEnabled']({
      ...selectedWarehouse.value
    })
  );

  const visitTypeToI18nKey = {
    [AssetVisitType.Live]: 'live',
    [AssetVisitType.LiveUnload]: 'live',
    [AssetVisitType.LiveLoad]: 'live',
    [AssetVisitType.Drop]: 'drop',
    [AssetVisitType.PickUp]: 'pickup',
    [AssetVisitType.DropHook]: 'dropHook'
  };

  const directionToI18nKey = {
    [AssetVisitDirection.Inbound]: 'inbound',
    [AssetVisitDirection.Outbound]: 'outbound',
    [AssetVisitDirection.InboundAndOutbound]: 'inboundAndOutbound'
  };

  const getAssetVisitTypeDisplayName = type => {
    const typeKey = visitTypeToI18nKey[type];
    if (!typeKey) {
      return type;
    }

    const typeName = t(`core.yard.assetVisitTypes.${typeKey}`);
    const direction = getDirectionForVisitType(type);
    if (!direction) {
      return typeName;
    }

    const dirKey = directionToI18nKey[direction];
    const directionName = t(`core.yard.assetVisitDirections.${dirKey}`);
    return `${typeName} · ${directionName}`;
  };

  const isLoadingSpots = computed(() => get(store, `${storePath}.isLoadingSpots`));
  const isLoadingAssetVisits = computed(() => get(store, `${storePath}.isLoadingAssetVisits`));

  // Read-write refs
  const selectedWarehouse = computed({
    get() {
      return get(store, `${storePath}.selectedWarehouse`);
    },
    set(value) {
      store.commit('Yard/setSelectedWarehouse', value || null);

      if (value?.id) {
        // Make sure all related data is fetched
        fetchAssetVisits();

        // Update warehouseId to the url
        router.push({
          params: { warehouseId: value.id }
        });
      } else {
        // Clean up the url
        router.push({
          name: 'yard'
        });
      }
    }
  });
  const selectedAreas = computed({
    get() {
      return get(store, `${storePath}.selectedAreas`);
    },
    set(value) {
      store.commit('Yard/setSelectedAreas', value || []);
    }
  });
  const selectedSpot = computed({
    get() {
      return get(store, `${storePath}.selectedSpot`);
    },
    set(value) {
      store.commit('Yard/setSelectedSpot', value || {});
      if (value?.id) {
        store
          .dispatch('Yard/fetchSpot', value.id)
          .then(spot => store.commit('Yard/setSelectedSpot', spot));

        nextTick(() => {
          const element = document.getElementById(value.id);

          if (element) {
            setTimeout(() => {
              element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center'
              });
            }, 500);
          }
        });
      }
    }
  });
  const selectedAssetVisit = computed({
    get() {
      return get(store, `${storePath}.selectedAssetVisit`);
    },
    set(value) {
      store.commit('Yard/setSelectedAssetVisit', value || null);
    }
  });
  const viewMode = computed({
    get() {
      return get(store, `${storePath}.viewMode`);
    },
    set(value) {
      store.dispatch('Yard/setViewMode', { value });
    }
  });

  // Route refs
  const defaultWarehouseId = computed(() =>
    isUUID(route.params?.warehouseId) ? String(route.params.warehouseId) : null
  );
  const defaultSpotAreaId = computed(() =>
    isUUID(route.params?.spotAreaId) ? String(route.params.spotAreaId) : null
  );

  const auth = useAuth();

  const cannotCreateSpotAssignment = computed(() => {
    const user = auth.meRef.value;
    return !canUserCreateSpotAssignment(user);
  });

  const canManageSpotsAndAreas = computed(() => {
    const user = auth.meRef.value;
    return canUserCreateSpot(user);
  });

  const canEditAssetForms = computed(() => {
    const user = auth.meRef.value;
    return isUserRoleGreaterOrEqual(user.role, UserRole.OPERATOR);
  });

  const canEditAssetContainerData = computed(() => {
    const user = auth.meRef.value;
    return isUserRoleGreaterOrEqual(user.role, UserRole.ATTENDANT);
  });

  const isReadonlyAccess = computed(() => {
    const user = auth.meRef.value;
    return isUserRoleLessOrEqual(user.role, UserRole.SPECTATOR);
  });

  // Actions
  const fakeCheckIn = data => store.dispatch('Yard/fakeCheckIn', data);
  const fakeCheckInUnplanned = data => store.dispatch('Yard/fakeCheckInUnplanned', data);
  const fetchPreviousYardData = () => store.dispatch('Yard/fetchPreviousYardData');
  const persistSelectedWarehouse = warehouseId =>
    store.dispatch('Yard/persistSelectedWarehouse', warehouseId);
  const detachAssetVisit = (
    assetVisitId,
    assetVisitAction,
    assetContainerActions,
    reassignData = null
  ) =>
    store.dispatch('Yard/detachAssetVisit', {
      assetVisitId,
      assetVisitAction,
      assetContainerActions,
      reassignData
    });
  const attachAssetVisit = (assetVisitId, assetContainerActions) =>
    store.dispatch('Yard/attachAssetVisit', {
      assetVisitId,
      assetContainerActions
    });
  const updateAssetContainer = (assetContainerId, data) =>
    store.dispatch('Yard/updateAssetContainer', { assetContainerId, data });
  const updateAssetContainerStatus = (assetContainerId, eventType) =>
    store.dispatch('Yard/updateAssetContainerStatus', {
      assetContainerId,
      eventType
    });
  const updateAssetVisitStatus = (assetVisitId, eventType) =>
    store.dispatch('Yard/updateAssetVisitStatus', { assetVisitId, eventType });
  const getAppointmentById = appointmentId =>
    store.dispatch('Appointments/getAppointmentById', appointmentId);
  const fetchAssetVisits = () => store.dispatch('Yard/fetchAssetVisits');
  const fetchSpot = id => store.dispatch('Yard/fetchSpot', id);
  const closeSpot = (id, data) => store.dispatch('Yard/closeSpot', { id, data });
  const openSpot = (id, data) => store.dispatch('Yard/openSpot', { id, data });
  const deleteSpotAssignment = id => store.dispatch('Yard/deleteSpotAssignment', id);
  const departSpotAssignment = (id, assetType) =>
    store.dispatch('Yard/departSpotAssignment', { id, assetType });
  const reassignSpotAssignment = (id, data) =>
    store.dispatch('Yard/reassignSpotAssignment', { id, data });

  const messageDriver = (assetVisitId, content) =>
    store.dispatch('Yard/messageDriver', { assetVisitId, content });
  const setIsLoadingAssetVisits = value => store.commit('Yard/setIsLoadingAssetVisits', value);

  const validateSpotCodeInput = value => {
    if (
      value?.length < 1 ||
      value?.length > YARD_SPOT_CODE_MAX_LENGTH ||
      !/^[a-zA-Z0-9]*$/.test(value)
    ) {
      return `Code must be between 1 and ${YARD_SPOT_CODE_MAX_LENGTH} characters and contains only letters and numbers (a-z,  0-9)`;
    }
    return true;
  };

  const validateSpotTypeInput = value => {
    if (!value) {
      return 'Spot type is required';
    }

    if (!Object.values(SpotType).includes(value)) {
      return `Spot type is not valid, valid choices are ${Object.values(SpotType).join(',')}`;
    }
    return true;
  };

  const formatDateTime = (date, amPmFormat, militaryFormat) =>
    formatDateTimeWithMilitarySupport(
      date,
      selectedWarehouse.value?.timezone,
      amPmFormat,
      isMilitaryTimeEnabled.value,
      militaryFormat,
      locale.value
    );

  const getTranslatedAssetContainerType = type => {
    return t(`core.yard.assetContainerTypes.${camelCase(type)}`);
  };

  const getTranslatedSpotStatus = spot => t(`core.yard.spotStatuses.${camelCase(spot.status)}`);

  const isSpotOpen = spot => spot.status === SpotStatus.OPEN;
  const isSpotClosed = spot => spot.status === SpotStatus.CLOSED;
  const isSpotAssigned = spot => spot.status === SpotStatus.ASSIGNED;
  const isSpotReserved = spot => SPOT_STATUSES_RESERVED.includes(spot.status);

  const hasAssignedAssetContainer = spot => {
    const toArray = value => (Array.isArray(value) ? value : []);
    const spotAssignments = toArray(spot?.spotAssignments);

    return spotAssignments.some(spotAssignment => {
      if (spotAssignment.assetContainer?.id) {
        return true;
      }

      if (toArray(spotAssignment.assetVisit?.assetContainers).some(ac => ac.id)) {
        return true;
      }
      return false;
    });
  };

  const doesSpotHaveLockedAsset = spot => {
    /**
     * We check this because, the origin spot for a move task - which is causes
     * an asset to be "locked" - is currently being set as closed (i.e., isActive = false).
     */
    if (!isSpotClosed(spot)) {
      return false;
    }

    return hasAssignedAssetContainer(spot);
  };

  return {
    // Read-Write Refs
    selectedWarehouseRef: selectedWarehouse,
    selectedAreasRef: selectedAreas,
    selectedSpotRef: selectedSpot,
    selectedAssetVisitRef: selectedAssetVisit,
    viewModeRef: viewMode,
    // Read-Only Refs
    isLoadingSpotsRef: isLoadingSpots,
    isLoadingAssetVisitsRef: isLoadingAssetVisits,
    defaultWarehouseIdRef: defaultWarehouseId,
    defaultSpotAreaIdRef: defaultSpotAreaId,
    // Methods
    fakeCheckIn,
    fakeCheckInUnplanned,
    attachAssetVisit,
    detachAssetVisit,
    updateAssetContainerStatus,
    updateAssetVisitStatus,
    getAppointmentById,
    fetchAssetVisits,
    getArrivedAssetContainerEvent,
    closeSpot,
    openSpot,
    fetchSpot,
    messageDriver,
    fetchPreviousYardData,
    deleteSpotAssignment,
    departSpotAssignment,
    reassignSpotAssignment,
    validateSpotCodeInput,
    validateSpotTypeInput,
    getAssetVisitTypeDisplayName,
    formatDateTime,
    getTruckThemeColor,
    isReadonlyAccess,
    sortSpots,
    relativeTimeFromWarehouse,
    getAssignedAssetContainerStatus,
    setIsLoadingAssetVisits,
    updateAssetContainer,
    isAssetContainer,
    isSpotOpen,
    isSpotClosed,
    isSpotAssigned,
    isSpotReserved,
    hasAssetVisitOnSpot: spot => isSpotAssignedToAssetVisit(spot),
    hasAssetContainerOnSpot: spot => isSpotAssignedToAssetContainer(spot),
    hasAssetVisitOnlyOnSpot: spot => isSpotAssignedOnlyToAssetVisit(spot),
    hasAssetContainerOnlyOnSpot: spot => isSpotAssignedOnlyToAssetContainer(spot),
    // Basic Data
    spotCardHeight,
    spotCardWidth,
    spotType: SpotType,
    spotStatus: SpotStatus,
    persistSelectedWarehouse,
    getAssignedSpotIcon,
    getAssetContainerStateIcon,
    getAssetContainerStatusIcon,
    getAssetContainerStatusColor,
    getSpotIcon,
    getTruckIcon,
    getLoadTypeDirectionColor,
    isYMSEnabledForSelectedWarehouse,
    isGateManagementEnabledForSelectedWarehouse,
    doesSpotHaveLockedAsset,
    cannotCreateSpotAssignmentRef: cannotCreateSpotAssignment,
    canManageSpotsAndAreasRef: canManageSpotsAndAreas,
    canEditAssetFormsRef: canEditAssetForms,
    canEditAssetContainerDataRef: canEditAssetContainerData,
    getSpotStatus,
    isMilitaryTimeEnabled,
    goToSpotFromOtherModules,
    truckThemeColors,
    YARD_SPOT_CODE_MAX_LENGTH,
    SPOT_ASSIGNMENT_CHANGED,
    GO_TO_SPOT,
    getTranslatedAssetContainerType,
    getTranslatedSpotStatus
  };
}
